import { sanitizeHtml } from "@/components/RichTextEditor";

const looksHtml = (s) => /<[a-z][\s\S]*>/i.test(s);

const decodeEntities = (s) => {
  const t = document.createElement("textarea");
  t.innerHTML = s;
  return t.value;
};

// Convert leftover markdown remnants (bold/italic/links/images) to HTML.
const mdInline = (s) =>
  s
    .replace(/\*\*([^*]{1,300}?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[\s(>])\*([^*\n]+)\*(?=[\s.,;:!?)<]|$)/g, "$1<em>$2</em>")
    .replace(/!\[([^\]]*)\]\((https?:[^)\s]+)\)/g, '<img alt="$1" src="$2" style="max-width:100%;border-radius:12px" />')
    .replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

// Minimal block-level markdown for plain-markdown bodies.
const mdToHtml = (s) => {
  const blocks = s.split(/\n{2,}/);
  return blocks
    .map((blk) => {
      const b = blk.trim();
      if (!b) return "";
      const h = b.match(/^(#{1,3})\s+(.*)$/s);
      if (h) return `<h${h[1].length + 1}>${mdInline(h[2])}</h${h[1].length + 1}>`;
      if (/^([-*]\s+.+\n?)+$/.test(b)) {
        const items = b.split(/\n/).map((l) => `<li>${mdInline(l.replace(/^[-*]\s+/, ""))}</li>`).join("");
        return `<ul>${items}</ul>`;
      }
      return `<p>${mdInline(b).replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");
};

// Remove document scaffolding that should never render as visible text.
const stripScaffolding = (s) =>
  s
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<\/?(html|body|meta|link|title)[^>]*>/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    // unclosed style/script: drop from the opening tag to the end of that line-block
    .replace(/<style[^>]*>[\s\S]*$/gi, "")
    .replace(/<script[^>]*>[\s\S]*$/gi, "");

/**
 * Normalizes any stored article body (HTML, escaped HTML, markdown, plain text)
 * into safe display HTML. Never mutates stored content — rendering only.
 * Returns { html } for rich bodies or { text } for plain paragraphs.
 */
export function renderArticleBody(body) {
  let b = (body || "").trim();
  // Escaped HTML (&lt;p&gt;…) — decode up to twice (handles double-escaping).
  for (let i = 0; i < 2 && !looksHtml(b) && /&lt;\s*[a-z!/]/i.test(b); i++) b = decodeEntities(b);
  if (looksHtml(b)) {
    // Escaped fragments living INSIDE an otherwise-HTML body (e.g. "&lt;div&gt;…&lt;/div&gt;")
    b = b.replace(/&lt;[^&\n]{1,120}&gt;/g, (m) => {
      const dec = decodeEntities(m);
      return /^<\/?[a-z![][^>]*>$/i.test(dec) ? "" : m; // drop stray raw-tag noise (incl. <!doctype>)
    });
    return { html: sanitizeHtml(mdInline(stripScaffolding(b)).replace(/\*\*/g, "")) };
  }
  if (/(^|\n)#{1,3}\s|\*\*[^*]+\*\*|\[[^\]]+\]\(https?:[^)]+\)/.test(b)) {
    return { html: sanitizeHtml(mdToHtml(b)) };
  }
  return { text: b };
}

const CODEY = /\{[^}]*;[^}]*\}|font-family|@context|@media|<!doctype|&lt;|"@type"|<\/?[a-z][^>]*>/i;

/** Display-safe excerpt: never shows CSS/JSON-LD/raw markup; falls back to body text. */
export function cleanExcerpt(article, max = 200) {
  let ex = decodeEntities((article?.excerpt || "").replace(/<[^>]+>/g, "")).trim();
  if (!ex || CODEY.test(ex)) {
    const r = renderArticleBody(article?.body || "");
    const src = r.html ? r.html.replace(/<[^>]+>/g, " ") : (r.text || "");
    ex = decodeEntities(src).replace(/\s+/g, " ").trim();
  }
  ex = ex.replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
  return ex.length > max ? ex.slice(0, max).replace(/\s+\S*$/, "") + "…" : ex;
}
